import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Download, ChevronDown, ChevronRight, Pencil, Plus, Lock, Unlock, ArrowUpCircle, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { salaryMatrixApi, salaryApi, adminApi, employeeApi } from '../lib/api';
import { toast } from 'sonner';
import { useUnit } from '../contexts/UnitContext';
import { useAuth } from '../contexts/AuthContext';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';

export default function SalarySheet() {
  const { activeUnit, isAllUnits } = useUnit();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'admin';
  const [data, setData] = useState({ years: [], employees: [], can_edit: false, locked_years: [] });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [showOlder, setShowOlder] = useState(false);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null); // { empId, year }
  const [editVal, setEditVal] = useState('');
  const [reviseFor, setReviseFor] = useState(null);
  const [revGross, setRevGross] = useState(''); const [revDate, setRevDate] = useState(''); const [revReason, setRevReason] = useState('');

  // new-cycle dialog
  const [cycleOpen, setCycleOpen] = useState(false);
  const [cyForm, setCyForm] = useState({ year: '', basis: 'avg_pct', flat_pct: '8', effective_date: '' });
  const [cyBusy, setCyBusy] = useState(false);

  // promotion staging + bulk letters
  const [staged, setStaged] = useState({});
  const [promoFor, setPromoFor] = useState(null);
  const [promoOptions, setPromoOptions] = useState([]);
  const [promoBusy, setPromoBusy] = useState(false);
  const [lockOpen, setLockOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [lockBusy, setLockBusy] = useState(false);
  const [genBusy, setGenBusy] = useState(false);
  const [genOpen, setGenOpen] = useState(false);
  const [genEff, setGenEff] = useState('');
  const [sigEmps, setSigEmps] = useState([]);
  const [sigName, setSigName] = useState('');
  const [sigTitle, setSigTitle] = useState('');
  const [sigEmpId, setSigEmpId] = useState('');
  const [sigOverrides, setSigOverrides] = useState({});   // {employee_id: {name, title}}
  const [genRecipients, setGenRecipients] = useState([]); // this cycle's letter recipients

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeUnit]);
  const load = async () => {
    setLoading(true); setLoadError(false);
    try {
      const res = await salaryMatrixApi.get({ unit: activeUnit });
      setData({ locked_years: [], ...res.data });
      const yrs = res.data.years || [];
      const ly = yrs.length ? yrs[yrs.length - 1] : null;
      if (ly) {
        try {
          const sp = await adminApi.stagedPromotions(parseInt(ly, 10));
          const map = {}; (sp.data.staged || []).forEach((x) => { map[x.employee_id] = x; });
          setStaged(map);
        } catch { setStaged({}); }
      } else { setStaged({}); }
    }
    catch (e) {
      if (e?.response?.status === 403) toast.error('You do not have access to salary data');
      else { toast.error('Failed to load salary sheet'); setLoadError(true); }
    } finally { setLoading(false); }
  };

  const allYears = data.years || [];
  const recentCount = 5;
  const olderYears = allYears.slice(0, Math.max(0, allYears.length - recentCount));
  const recentYears = allYears.slice(Math.max(0, allYears.length - recentCount));
  const visibleYears = showOlder ? allYears : recentYears;
  const latestYear = allYears.length ? allYears[allYears.length - 1] : null;
  // Average increment and increase% per year, across employees who have a record that year.
  const yearAverages = {};
  allYears.forEach((y) => {
    let incSum = 0, pctSum = 0, grossSum = 0, n = 0;
    (data.employees || []).forEach((emp) => {
      const c = (emp.years || {})[y];
      if (c && c.gross != null) { incSum += (c.increment || 0); pctSum += (c.pct || 0); grossSum += c.gross; n += 1; }
    });
    const base = grossSum - incSum;   // Σ prior-year gross = current gross minus this year's increment
    yearAverages[y] = n ? {
      avgInc: incSum / n, avgPct: pctSum / n, n,
      totalInc: incSum,
      wPct: base > 0 ? (incSum / base) * 100 : null,   // payroll-weighted increase, ties to the grand totals
    } : null;
  });
  const yearTotals = {};
  allYears.forEach((y) => {
    let sum = 0, has = false;
    (data.employees || []).forEach((emp) => { const c = (emp.years || {})[y]; if (c && c.gross != null) { sum += c.gross; has = true; } });
    yearTotals[y] = has ? sum : null;
  });
  const lockedYears = data.locked_years || [];
  const isYearLocked = (y) => lockedYears.includes(String(y));
  const latestLocked = latestYear ? isYearLocked(latestYear) : false;

  const fmt = (n) => (n === null || n === undefined || n === '' ? '—' : Number(n).toLocaleString('en-IN'));
  const fmtPct = (n) => (n === null || n === undefined || n === '' ? '—' : `${Number(n).toFixed(1)}%`);
  const fmtDate = (s2) => { if (!s2) return '—'; const q = String(s2).slice(0, 10).split('-'); return q.length === 3 ? `${q[2]}-${q[1]}-${q[0]}` : s2; };
  const baseGrossOf = (emp, y) => {
    const c = emp.years[y] || {};
    const prev = emp.years[String(parseInt(y, 10) - 1)];
    if (prev && prev.gross != null) return prev.gross;
    if (c.gross != null && c.increment != null) return c.gross - c.increment;
    return 0;
  };

  const startEdit = (empId, year, currentInc) => {
    if (!data.can_edit || String(year) !== String(latestYear) || isYearLocked(year)) return;
    setEditing({ empId, year }); setEditVal(currentInc ?? 0);
  };
  const saveEdit = async (emp) => {
    if (!editing) return;
    const { empId, year } = editing;
    const inc = parseFloat(editVal);
    if (isNaN(inc)) { setEditing(null); return; }
    const gross = baseGrossOf(emp, year) + inc;
    try {
      await salaryApi.update(empId, year, { year: parseInt(year, 10), gross });
      toast.success('Salary updated'); setEditing(null); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Update failed'); }
  };

  const submitRevision = async () => {
    if (!reviseFor || !latestYear) return;
    const val = parseFloat(revGross);
    if (isNaN(val) || !revDate) { toast.error('Enter new gross and effective date'); return; }
    try {
      await salaryApi.revise(reviseFor.id, parseInt(latestYear, 10), { new_gross: val, effective_date: revDate, reason: revReason });
      toast.success('Mid-cycle revision recorded'); setReviseFor(null); setRevGross(''); setRevDate(''); setRevReason(''); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Revision failed'); }
  };

  const openCycle = () => {
    const next = latestYear ? String(parseInt(latestYear, 10) + 1) : String(new Date().getFullYear());
    setCyForm({ year: next, basis: 'avg_pct', flat_pct: '8', effective_date: `${next}-04-01` });
    setCycleOpen(true);
  };
  const submitCycle = async () => {
    const year = parseInt(cyForm.year, 10);
    if (!year) { toast.error('Enter a valid year'); return; }
    if (isAllUnits && !window.confirm(`You are in All Units mode. Open cycle ${year} for EVERY unit (group-wide)?`)) return;
    setCyBusy(true);
    try {
      const payload = { year, basis: cyForm.basis, effective_date: cyForm.effective_date || null,
                        flat_pct: cyForm.basis === 'flat_pct' ? parseFloat(cyForm.flat_pct) : null,
                        unit: isAllUnits ? null : (activeUnit || null) };
      const res = await adminApi.createSalaryCycle(payload);
      toast.success(`Cycle ${year}: ${res.data.created} pre-filled, ${res.data.skipped_existing} already existed`);
      setCycleOpen(false); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not create cycle'); }
    finally { setCyBusy(false); }
  };
  // ---- promotion staging ----
  const openPromo = async (emp) => {
    setPromoFor(emp); setPromoOptions([]);
    try { const r = await adminApi.nextDesignations(emp.level || 5); setPromoOptions(r.data.options || []); }
    catch { setPromoOptions([]); }
  };
  const stagePromo = async (title) => {
    if (!promoFor || !latestYear) return;
    setPromoBusy(true);
    try {
      await adminApi.stagePromotion(parseInt(latestYear, 10), { employee_id: promoFor.id, to_designation: title });
      toast.success(`${promoFor.name} staged → ${title}`);
      setPromoFor(null); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not stage promotion'); }
    finally { setPromoBusy(false); }
  };
  const unstagePromo = async (empId) => {
    if (!latestYear) return;
    try { await adminApi.unstagePromotion(parseInt(latestYear, 10), empId); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not remove staged promotion'); }
  };

  // ---- lock (commits staged promotions) ----
  const openLock = async () => {
    if (!latestYear) return;
    try { const r = await adminApi.letterPreview(parseInt(latestYear, 10), activeUnit || undefined); setPreview(r.data); setLockOpen(true); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not load lock preview'); }
  };
  const confirmLock = async () => {
    if (!latestYear) return;
    setLockBusy(true);
    try {
      const r = await adminApi.lockSalaryCycle(parseInt(latestYear, 10));
      toast.success(`Cycle ${latestYear} locked — ${r.data.promotions_committed || 0} promotion(s) committed`);
      setLockOpen(false); setPreview(null); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not lock cycle'); }
    finally { setLockBusy(false); }
  };
  const unlockCycle = async () => {
    if (!latestYear) return;
    if (!window.confirm(`Unlock ${latestYear}? Salaries become editable again. Promotions already committed stay applied — unlocking does not revert designation changes.`)) return;
    try { await adminApi.unlockSalaryCycle(parseInt(latestYear, 10)); toast.success(`Cycle ${latestYear} unlocked`); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not unlock cycle'); }
  };

  // ---- bulk letters (after lock) ----
  const loadLogoDataUrl = async () => {
    // Inline the app logo as a data URI so it renders in the print tab (no origin/CORS issues).
    try {
      const resp = await fetch('/assets/aampl-logo.jpg');
      if (!resp.ok) return null;
      const blob = await resp.blob();
      return await new Promise((res) => {
        const fr = new FileReader();
        fr.onload = () => res(fr.result);
        fr.onerror = () => res(null);
        fr.readAsDataURL(blob);
      });
    } catch { return null; }
  };
  const openGen = async () => {
    if (!latestYear) return;
    setGenEff(`${latestYear}-04-01`);
    setSigName(''); setSigTitle(''); setSigEmpId(''); setSigOverrides({});
    setGenOpen(true);
    if (sigEmps.length === 0) {
      try {
        const r = await employeeApi.list();
        // Signing authority: Manager & above (level 8+ on the ladder) or leadership.
        const eligible = (r.data || []).filter((e) => e.status !== 'Left' && ((e.level || 0) >= 8 || e.is_leadership));
        eligible.sort((a, b) => (b.level || 0) - (a.level || 0) || (a.name || '').localeCompare(b.name || ''));
        setSigEmps(eligible);
      } catch { /* non-fatal */ }
    }
    // Recipients of this cycle's letters (increments + staged/committed promotions), for per-person overrides.
    try {
      const p = await adminApi.letterPreview(parseInt(latestYear, 10));
      const seen = {};
      (p.data.increment_recipients || []).forEach((x) => { seen[x.employee_id] = { id: x.employee_id, name: x.name, department: x.department }; });
      (p.data.staged_promotions || []).forEach((x) => { const id = x.employee_id || x.id; if (id && !seen[id]) seen[id] = { id, name: x.name, department: x.department }; });
      setGenRecipients(Object.values(seen).sort((a, b) => (a.name || '').localeCompare(b.name || '')));
    } catch { setGenRecipients([]); }
  };
  const pickSignatory = (id) => {
    const emp = sigEmps.find((x) => x.id === id);
    setSigEmpId(id || '');
    if (emp) { setSigName(emp.name || ''); setSigTitle(emp.designation || ''); }
  };
  const setOverrideFor = (empId, signerId) => {
    setSigOverrides((s) => {
      const next = { ...s };
      const signer = sigEmps.find((x) => x.id === signerId);
      if (signer) next[empId] = { name: signer.name || '', title: signer.designation || '' };
      else delete next[empId];
      return next;
    });
  };
  const runGenerate = async () => {
    if (!latestYear) return;
    setGenBusy(true);
    try {
      const logoUrl = await loadLogoDataUrl();
      const payload = { unit: activeUnit || null, effective_date: genEff || null,
                        signatory_name: sigName || null, signatory_title: sigTitle || null,
                        signatory_overrides: Object.keys(sigOverrides).length ? sigOverrides : null };
      if (logoUrl) payload.logo_url = logoUrl;
      const r = await adminApi.bulkLetters(parseInt(latestYear, 10), payload);
      setGenOpen(false);
      if (!r.data.html) { toast.message(r.data.message || 'No letters to generate'); return; }
      const w = window.open('', '_blank');
      if (w) { w.document.open(); w.document.write(r.data.html); w.document.close(); }
      else toast.error('Pop-up blocked — allow pop-ups to view the letters');
      toast.success(`${r.data.increment_letters} increment + ${r.data.promotion_letters} promotion letter(s)`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not generate letters'); }
    finally { setGenBusy(false); }
  };

  const exportCsv = () => {
    const header = isAllUnits
      ? ['Employee', 'Card', 'Unit', 'Department', 'Designation', 'Starting Salary']
      : ['Employee', 'Card', 'Department', 'Designation', 'Starting Salary'];
    allYears.forEach((y) => header.push(`${y} Inc`, `${y} Gross`, `${y} %`));
    const lines = [header.join(',')];
    data.employees.forEach((emp) => {
      const row = isAllUnits
        ? [emp.name, emp.card_no || '', emp.unit || '', emp.department || '', emp.designation || '', emp.starting_salary ?? '']
        : [emp.name, emp.card_no || '', emp.department || '', emp.designation || '', emp.starting_salary ?? ''];
      allYears.forEach((y) => { const c = emp.years[y] || {}; row.push(c.increment ?? '', c.gross ?? '', c.pct ?? ''); });
      lines.push(row.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','));
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = 'salary-sheet.csv'; a.click(); URL.revokeObjectURL(url);
  };

  const YearCells = ({ emp, y, c }) => {
    const isEditing = editing && editing.empId === emp.id && String(editing.year) === String(y);
    const propBg = c.is_proposed ? 'bg-[#FBF7EC]' : '';
    const isLatest = String(y) === String(latestYear);
    const editable = data.can_edit && isLatest && !isYearLocked(y);
    const base = baseGrossOf(emp, y);
    const inc = isEditing ? Number(editVal || 0) : c.increment;
    const gross = isEditing ? base + Number(editVal || 0) : c.gross;
    const pct = isEditing ? (base ? (Number(editVal || 0) / base) * 100 : 0) : c.pct;
    return (
      <React.Fragment>
        {isEditing ? (
          <td className={`py-1 px-2 text-right ${propBg}`}>
            <input autoFocus type="number" value={editVal}
              onChange={(e) => setEditVal(e.target.value)}
              onBlur={() => saveEdit(emp)}
              onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(emp); if (e.key === 'Escape') setEditing(null); }}
              className="w-20 text-right border border-[#2563EB] rounded px-1 py-0.5 text-sm" />
          </td>
        ) : (
          <td className={`py-2 px-3 text-right tabular-nums ${propBg} ${editable ? 'cursor-pointer hover:ring-1 hover:ring-[#2563EB]/40 font-medium text-[#11182B]' : 'text-[#5A6377]'}`}
            onClick={() => { if (editable) startEdit(emp.id, y, c.increment); }}
            title={editable ? 'Click to edit the increase' : (isYearLocked(y) ? 'Locked cycle' : (data.can_edit ? 'Locked — past year' : ''))}>
            {fmt(inc)}
          </td>
        )}
        <td className={`py-2 px-3 text-right tabular-nums ${c.is_proposed ? 'font-semibold' : ''} ${isEditing ? 'bg-[#E8F2FE] font-semibold text-[#15307F]' : propBg}`}>
          {fmt(gross)}{c.revised ? <span className="text-[#E11D2A] ml-1" title="Mid-cycle revised">●</span> : null}
        </td>
        <td className={`py-2 px-3 text-right tabular-nums text-[#5A6377] border-r border-[#E6E9F0] ${isEditing ? 'bg-[#E8F2FE]' : propBg}`}>{fmtPct(pct)}</td>
      </React.Fragment>
    );
  };

  return (
    <div data-testid="salary-sheet-page" className="flex h-screen app-shell">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Header />
        <main className="flex-1 overflow-auto p-5 sm:p-6 lg:p-8 page-enter">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-semibold tracking-tight text-[#11182B]">Salary Sheet</h1>
              <p className="text-sm text-[#5A6377] mt-1">Year-by-year salary progression.{data.can_edit ? ' Edit the Increase on the latest open year — Gross and % update live.' : ''}</p>
            </div>
            <div className="flex gap-2">
              {data.can_edit && (
                <Button data-testid="new-cycle-button" variant="outline" onClick={openCycle}><Plus className="w-4 h-4 mr-1" />New Cycle</Button>
              )}
              {data.can_edit && latestYear && !latestLocked && (
                <Button data-testid="lock-cycle-button" variant="outline" onClick={openLock}><Lock className="w-4 h-4 mr-1" />Lock {latestYear}</Button>
              )}
              {data.can_edit && latestYear && latestLocked && isSuperAdmin && (
                <Button data-testid="unlock-cycle-button" variant="outline" onClick={unlockCycle}><Unlock className="w-4 h-4 mr-1" />Unlock {latestYear}</Button>
              )}
              {data.can_edit && latestYear && (
                <Button data-testid="generate-letters-button" variant="outline" onClick={openGen} disabled={genBusy}><FileText className="w-4 h-4 mr-1" />{genBusy ? 'Generating…' : 'Generate Letters'}</Button>
              )}
              {olderYears.length > 0 && (
                <Button variant="outline" onClick={() => setShowOlder((s) => !s)}>
                  {showOlder ? <ChevronRight className="w-4 h-4 mr-1" /> : <ChevronDown className="w-4 h-4 mr-1" />}
                  {showOlder ? 'Hide older years' : `Show older years (${olderYears.length})`}
                </Button>
              )}
              <Button data-testid="export-csv-button" onClick={exportCsv}><Download className="w-4 h-4 mr-1" />Export CSV</Button>
            </div>
          </div>

          {!loading && !loadError && data.employees.length > 0 && (
            <div className="mb-4">
              <Input data-testid="salary-search" placeholder="Search by name, card, department…" value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-xs border-[#E6E9F0]" />
            </div>
          )}
          {loading ? <div className="text-sm text-[#5A6377]">Loading…</div>
            : loadError ? <div className="bg-white border border-[#F5C9CC] rounded-xl p-8 text-center text-sm text-[#C2151F]">Couldn&rsquo;t load salary data — please reload.</div>
            : data.employees.length === 0 ? <div className="bg-white border border-[#E6E9F0] rounded-xl p-8 text-center text-sm text-[#5A6377]">No salary data for this unit / your access level.</div>
            : (
            <div className="bg-white border border-[#E6E9F0] rounded-xl overflow-auto">
              <table className="border-collapse text-sm w-full">
                <thead>
                  <tr className="bg-[#102468] text-white text-xs font-semibold uppercase tracking-wide">
                    <th className="sticky left-0 z-20 bg-[#102468] text-left py-2 px-3 border-b border-r border-[#E6E9F0] w-[220px] min-w-[220px]">Employee</th>
                    <th className="sticky left-[220px] z-20 bg-[#102468] text-left py-2 px-3 border-b border-[#E6E9F0] w-[120px] min-w-[120px] whitespace-nowrap">Date of Joining</th>
                    <th className="sticky left-[340px] z-20 bg-[#102468] text-left py-2 px-3 border-b border-[#E6E9F0] w-[150px] min-w-[150px] whitespace-nowrap">Department</th>
                    <th className="sticky left-[490px] z-20 bg-[#102468] text-left py-2 px-3 border-b border-r border-[#E6E9F0] w-[190px] min-w-[190px] whitespace-nowrap">Designation</th>
                    <th rowSpan={2} className="text-right py-2 px-3 border-b border-r border-[#E6E9F0] whitespace-nowrap align-middle">Starting<br/>Salary</th>
                    {visibleYears.map((y) => (
                      <th key={y} colSpan={3} className="text-center py-2 px-3 border-b border-r border-[#E6E9F0] whitespace-nowrap">
                        {y}{isYearLocked(y) && <Lock className="w-3 h-3 inline ml-1 mb-0.5" />}
                      </th>
                    ))}
                  </tr>
                  <tr className="bg-[#102468] text-[#AFC0E8] text-[10px] font-semibold uppercase">
                    <th className="sticky left-0 z-20 bg-[#102468] border-b border-r border-[#E6E9F0]"></th>
                    <th className="sticky left-[220px] z-20 bg-[#102468] border-b border-[#E6E9F0] w-[120px] min-w-[120px]"></th>
                    <th className="sticky left-[340px] z-20 bg-[#102468] border-b border-[#E6E9F0]"></th>
                    <th className="sticky left-[490px] z-20 bg-[#102468] border-b border-r border-[#E6E9F0] w-[190px] min-w-[190px]"></th>
                    {visibleYears.map((y) => (
                      <React.Fragment key={y}>
                        <th className="text-right py-1 px-3 border-b border-[#E6E9F0]">Inc</th>
                        <th className="text-right py-1 px-3 border-b border-[#E6E9F0]">Gross</th>
                        <th className="text-right py-1 px-3 border-b border-r border-[#E6E9F0]">%</th>
                      </React.Fragment>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(search
                    ? data.employees.filter((e) => {
                        const q = search.toLowerCase();
                        return (e.name || '').toLowerCase().includes(q) || (e.card_no || '').toLowerCase().includes(q)
                          || (e.department || '').toLowerCase().includes(q) || (e.designation || '').toLowerCase().includes(q);
                      })
                    : data.employees).map((emp) => (
                    <tr key={emp.id} data-testid={`salary-row-${emp.id}`} className="hover:bg-[#F7F8FB] border-b border-[#E6E9F0] last:border-0">
                      <td className="sticky left-0 z-10 bg-white py-2 px-3 border-r border-[#E6E9F0] font-medium text-[#11182B] whitespace-nowrap w-[220px] min-w-[220px]">
                        <div className="flex items-center justify-between gap-2">
                          <Link to={`/employees/${emp.id}`} className="hover:text-[#2563EB]">{emp.name}</Link>
                          {data.can_edit && (
                            <button data-testid={`revise-button-${emp.id}`} onClick={() => { setReviseFor(emp); setRevGross(''); setRevDate(''); setRevReason(''); }} className="text-[10px] text-[#2563EB] hover:underline flex items-center gap-0.5" title="Off-cycle dated revision"><Pencil className="w-3 h-3" />Revise</button>
                          )}
                        </div>
                        <div className="text-[10px] text-[#98A1B2]">{emp.card_no || ''}</div>
                        {isAllUnits && <div className="text-[10px] font-semibold text-[#15307F]">{emp.unit || ''}</div>}
                      </td>
                      <td className="sticky left-[220px] z-10 bg-white py-2 px-3 text-[#5A6377] whitespace-nowrap w-[120px] min-w-[120px]">{fmtDate(emp.doj)}</td>
                      <td className="sticky left-[340px] z-10 bg-white py-2 px-3 text-[#5A6377] whitespace-nowrap w-[150px] min-w-[150px]">{emp.department}</td>
                      <td className="sticky left-[490px] z-10 bg-white py-2 px-3 text-[#5A6377] border-r border-[#E6E9F0] whitespace-nowrap w-[190px] min-w-[190px]">
                        <div className="flex items-center gap-1.5">
                          <span>{emp.designation}</span>
                          {emp.level ? <span className="text-[9px] text-[#98A1B2]">L{emp.level}</span> : null}
                          {staged[emp.id] ? (
                            <span className="inline-flex items-center gap-1 text-[10px] bg-[#FBF3D9] text-[#8A6D1B] rounded px-1.5 py-0.5" title="Promotion staged — commits when you Lock the cycle">
                              →&nbsp;{staged[emp.id].to_designation}
                              {data.can_edit && !latestLocked && (
                                <button onClick={() => unstagePromo(emp.id)} className="font-bold hover:text-[#E11D2A]" title="Remove staged promotion">×</button>
                              )}
                            </span>
                          ) : (data.can_edit && latestYear && !latestLocked && (emp.level || 0) < 11 && (
                            <button data-testid={`promote-button-${emp.id}`} onClick={() => openPromo(emp)} className="text-[10px] text-[#2563EB] hover:underline flex items-center gap-0.5" title="Stage a promotion (one level up)">
                              <ArrowUpCircle className="w-3 h-3" />Promotion
                            </button>
                          ))}
                        </div>
                      </td>
                      <td className="py-2 px-3 text-right tabular-nums text-[#11182B] font-medium border-r border-[#E6E9F0] whitespace-nowrap bg-[#F7F8FB]">{emp.starting_salary != null ? `₹${fmt(emp.starting_salary)}` : '—'}</td>
                      {visibleYears.map((y) => <YearCells key={y} emp={emp} y={y} c={emp.years[y] || {}} />)}
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-[#EFF1F6] text-[#11182B] font-semibold border-t-2 border-[#102468]">
                    <td className="sticky left-0 z-10 bg-[#EFF1F6] py-2 px-3 border-r border-[#E6E9F0] text-[11px] uppercase tracking-wide whitespace-nowrap w-[220px] min-w-[220px]" title="Total increment ÷ total prior-year gross (rupee-weighted, not a plain average of individual %)">Overall increase</td>
                    <td className="sticky left-[220px] z-10 bg-[#EFF1F6] w-[120px] min-w-[120px]"></td>
                    <td className="sticky left-[340px] z-10 bg-[#EFF1F6] w-[150px] min-w-[150px]"></td>
                    <td className="sticky left-[490px] z-10 bg-[#EFF1F6] border-r border-[#E6E9F0] w-[190px] min-w-[190px]"></td>
                    <td className="bg-[#EFF1F6] border-r border-[#E6E9F0]"></td>
                    {visibleYears.map((y) => {
                      const a = yearAverages[y];
                      return (
                        <React.Fragment key={y}>
                          <td className="py-2 px-3 text-right tabular-nums text-[#5A6377]">{a ? fmt(Math.round(a.totalInc)) : '—'}</td>
                          <td className="py-2 px-3"></td>
                          <td className="py-2 px-3 text-right tabular-nums text-[#15A36E] border-r border-[#E6E9F0]">{a && a.wPct != null ? `${a.wPct.toFixed(1)}%` : '—'}</td>
                        </React.Fragment>
                      );
                    })}
                  </tr>
                  <tr className="bg-[#F4F6FA] text-[#5A6377]">
                    <td className="sticky left-0 z-10 bg-[#F4F6FA] py-1.5 px-3 border-r border-[#E6E9F0] text-[11px] whitespace-nowrap w-[220px] min-w-[220px]" title="Plain average of each employee's increment and %, unweighted (each person counts equally)">Avg / employee</td>
                    <td className="sticky left-[220px] z-10 bg-[#F4F6FA] w-[120px] min-w-[120px]"></td>
                    <td className="sticky left-[340px] z-10 bg-[#F4F6FA] w-[150px] min-w-[150px]"></td>
                    <td className="sticky left-[490px] z-10 bg-[#F4F6FA] border-r border-[#E6E9F0] w-[190px] min-w-[190px]"></td>
                    <td className="bg-[#F4F6FA] border-r border-[#E6E9F0]"></td>
                    {visibleYears.map((y) => {
                      const a = yearAverages[y];
                      return (
                        <React.Fragment key={y}>
                          <td className="py-1.5 px-3 text-right tabular-nums">{a ? fmt(Math.round(a.avgInc)) : '—'}</td>
                          <td className="py-1.5 px-3"></td>
                          <td className="py-1.5 px-3 text-right tabular-nums border-r border-[#E6E9F0]">{a ? `${a.avgPct.toFixed(1)}%` : '—'}</td>
                        </React.Fragment>
                      );
                    })}
                  </tr>
                  <tr className="bg-[#EFF1F6] text-[#11182B] font-bold">
                    <td className="sticky left-0 z-10 bg-[#EFF1F6] py-2 px-3 border-r border-[#E6E9F0] text-[11px] uppercase tracking-wide whitespace-nowrap w-[220px] min-w-[220px]">Total salary</td>
                    <td className="sticky left-[220px] z-10 bg-[#EFF1F6] w-[120px] min-w-[120px]"></td>
                    <td className="sticky left-[340px] z-10 bg-[#EFF1F6] w-[150px] min-w-[150px]"></td>
                    <td className="sticky left-[490px] z-10 bg-[#EFF1F6] border-r border-[#E6E9F0] w-[190px] min-w-[190px]"></td>
                    <td className="bg-[#EFF1F6] border-r border-[#E6E9F0]"></td>
                    {visibleYears.map((y) => (
                      <React.Fragment key={y}>
                        <td className="py-2 px-3"></td>
                        <td className="py-2 px-3 text-right tabular-nums text-[#11182B]">{yearTotals[y] != null ? `₹${fmt(yearTotals[y])}` : '—'}</td>
                        <td className="py-2 px-3 border-r border-[#E6E9F0]"></td>
                      </React.Fragment>
                    ))}
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {/* Off-cycle revision */}
          <Dialog open={!!reviseFor} onOpenChange={(o) => { if (!o) setReviseFor(null); }}>
            <DialogContent>
              <DialogHeader><DialogTitle>Off-cycle revision{reviseFor ? ` — ${reviseFor.name}` : ''}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <p className="text-xs text-[#5A6377]">Dated salary change within {latestYear} for one person (e.g. a raise effective October). Original preserved in history.</p>
                <div><Label>New gross (₹)</Label><Input data-testid="revision-gross-input" type="number" value={revGross} onChange={(e) => setRevGross(e.target.value)} /></div>
                <div><Label>Effective date</Label><Input data-testid="revision-date-input" type="date" value={revDate} onChange={(e) => setRevDate(e.target.value)} /></div>
                <div><Label>Reason (optional)</Label><Input data-testid="revision-reason-input" value={revReason} onChange={(e) => setRevReason(e.target.value)} placeholder="e.g. mid-year promotion" /></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setReviseFor(null)}>Cancel</Button>
                <Button data-testid="submit-revision-button" onClick={submitRevision}>Save revision</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* New appraisal cycle */}
          <Dialog open={cycleOpen} onOpenChange={setCycleOpen}>
            <DialogContent>
              <DialogHeader><DialogTitle>New appraisal cycle</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <p className="text-xs text-[#5A6377]">Creates the year&rsquo;s Inc/Gross columns for everyone in scope, pre-filled with a suggested increment. You can then edit any cell, and lock the cycle when final.</p>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Year</Label><Input data-testid="cycle-year" type="number" value={cyForm.year} onChange={(e) => setCyForm({ ...cyForm, year: e.target.value })} /></div>
                  <div><Label>Effective date</Label><Input data-testid="cycle-date" type="date" value={cyForm.effective_date} onChange={(e) => setCyForm({ ...cyForm, effective_date: e.target.value })} /></div>
                </div>
                <div>
                  <Label>Suggestion basis</Label>
                  <select data-testid="cycle-basis" className="border border-[#E6E9F0] rounded-md bg-white text-sm px-2 py-2 w-full"
                          value={cyForm.basis} onChange={(e) => setCyForm({ ...cyForm, basis: e.target.value })}>
                    <option value="avg_pct">Average % of each person&rsquo;s past increases</option>
                    <option value="avg_inc">Average ₹ of each person&rsquo;s past increases</option>
                    <option value="flat_pct">Flat % for everyone</option>
                  </select>
                </div>
                {cyForm.basis === 'flat_pct' && (
                  <div><Label>Flat %</Label><Input data-testid="cycle-flat" type="number" value={cyForm.flat_pct} onChange={(e) => setCyForm({ ...cyForm, flat_pct: e.target.value })} /></div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setCycleOpen(false)}>Cancel</Button>
                <Button data-testid="submit-cycle-button" onClick={submitCycle} disabled={cyBusy}>{cyBusy ? 'Creating…' : 'Create cycle'}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Promotion picker */}
          <Dialog open={!!promoFor} onOpenChange={(o) => { if (!o) setPromoFor(null); }}>
            <DialogContent>
              <DialogHeader><DialogTitle>Promote{promoFor ? ` — ${promoFor.name}` : ''}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                {promoFor && (
                  <p className="text-xs text-[#5A6377]">Current: <strong>{promoFor.designation}</strong> (L{promoFor.level || '—'}). Select the new designation — one level up only. Staged now; takes effect across the master, org chart and letters when you Lock {latestYear}.</p>
                )}
                {promoOptions.length === 0 ? (
                  <p className="text-sm text-[#98A1B2]">No higher designation available for this level.</p>
                ) : (
                  <div className="flex flex-col gap-2">
                    {promoOptions.map((opt) => (
                      <button key={opt} data-testid={`promo-option-${opt}`} disabled={promoBusy} onClick={() => stagePromo(opt)}
                        className="text-left border border-[#E6E9F0] rounded-md px-3 py-2 text-sm hover:border-[#2563EB] hover:bg-[#F2F7FF] disabled:opacity-50">
                        {opt}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setPromoFor(null)}>Cancel</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Generate letters — pick w.e.f date */}
          <Dialog open={genOpen} onOpenChange={(o) => { if (!o) setGenOpen(false); }}>
            <DialogContent>
              <DialogHeader><DialogTitle>Generate letters — {latestYear}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <p className="text-xs text-[#5A6377]">Set the effective (w.e.f) date printed on every letter in this batch. It appears in bold on each letter.</p>
                <div><Label>Effective date (w.e.f)</Label><Input data-testid="gen-eff-date" type="date" value={genEff} onChange={(e) => setGenEff(e.target.value)} /></div>
                <div className="pt-1 border-t border-[#F0F2F6]">
                  <Label>Signing authority</Label>
                  <select data-testid="gen-signatory-emp" className="border border-[#E6E9F0] rounded-md bg-white text-sm px-2 py-2 w-full mb-2"
                          defaultValue="" onChange={(e) => pickSignatory(e.target.value)}>
                    <option value="">— pick an employee (auto-fills designation) —</option>
                    {sigEmps.map((x) => <option key={x.id} value={x.id}>{x.name}{x.designation ? ` — ${x.designation}` : ''}</option>)}
                  </select>
                  <div className="grid grid-cols-2 gap-2">
                    <Input data-testid="gen-sig-name" value={sigName} onChange={(e) => setSigName(e.target.value)} placeholder="Signed by (name)" />
                    <Input data-testid="gen-sig-title" value={sigTitle} onChange={(e) => setSigTitle(e.target.value)} placeholder="Designation" />
                  </div>
                  <p className="text-[11px] text-[#98A1B2] mt-1">Leave blank to use your own account name. Only Manager &amp; above are listed.</p>
                </div>
                {genRecipients.length > 0 && (
                  <div className="pt-1 border-t border-[#F0F2F6]">
                    <Label>Per-employee signatory (optional overrides)</Label>
                    <p className="text-[11px] text-[#98A1B2] mb-1">The signer above applies to everyone; change individual people here.</p>
                    <div className="max-h-48 overflow-y-auto border border-[#E6E9F0] rounded-md divide-y divide-[#F0F2F6]">
                      {genRecipients.map((rcp) => {
                        const isSelf = sigEmpId && rcp.id === sigEmpId && !sigOverrides[rcp.id];
                        return (
                          <div key={rcp.id} className={`flex items-center justify-between gap-2 px-2 py-1.5 ${isSelf ? 'bg-[#FDE7E9]' : ''}`}>
                            <div className="min-w-0">
                              <div className="text-xs font-medium text-[#11182B] truncate">{rcp.name}</div>
                              <div className="text-[10px] text-[#98A1B2] truncate">{rcp.department || ''}{isSelf ? ' · cannot sign own letter — pick a signer' : ''}</div>
                            </div>
                            <select data-testid={`sig-override-${rcp.id}`} className="border border-[#E6E9F0] rounded-md bg-white text-xs px-1.5 py-1 max-w-[180px]"
                                    value={sigOverrides[rcp.id] ? (sigEmps.find((x) => x.name === sigOverrides[rcp.id].name)?.id || '') : ''}
                                    onChange={(e) => setOverrideFor(rcp.id, e.target.value)}>
                              <option value="">— default signer —</option>
                              {sigEmps.filter((x) => x.id !== rcp.id).map((x) => <option key={x.id} value={x.id}>{x.name}{x.designation ? ` — ${x.designation}` : ''}</option>)}
                            </select>
                          </div>
                        );
                      })}
                    </div>
                    {sigEmpId && genRecipients.some((r) => r.id === sigEmpId) && !sigOverrides[sigEmpId] && (
                      <p className="text-[11px] text-[#E11D2A] mt-1 font-medium">The chosen signatory is also receiving a letter — select a different signer for their own letter above before generating.</p>
                    )}
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setGenOpen(false)}>Cancel</Button>
                <Button data-testid="run-generate-button" onClick={runGenerate} disabled={genBusy || (sigEmpId && genRecipients.some((r) => r.id === sigEmpId) && !sigOverrides[sigEmpId])}>{genBusy ? 'Generating…' : 'Generate'}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Lock confirm + preview */}
          <Dialog open={lockOpen} onOpenChange={(o) => { if (!o) { setLockOpen(false); setPreview(null); } }}>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>Lock {latestYear} — confirm</DialogTitle></DialogHeader>
              {preview ? (
                <div className="space-y-3 text-sm">
                  <p className="text-xs text-[#5A6377]">Locking makes {latestYear} read-only and <strong>commits all staged promotions</strong> (designation changes apply across the master, org chart and letters). This cannot be undone from here.</p>
                  <div className="rounded-md border border-[#E6E9F0] p-3">
                    <div className="font-semibold text-[#11182B]">{(preview.staged_promotions || []).length} promotion(s) will commit</div>
                    {(preview.staged_promotions || []).length > 0 && (
                      <ul className="mt-1 space-y-0.5 text-[#5A6377] max-h-28 overflow-auto">
                        {preview.staged_promotions.map((sp) => (
                          <li key={sp.employee_id}>{sp.employee_name}: {sp.from_designation} → <strong>{sp.to_designation}</strong></li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div className="rounded-md border border-[#E6E9F0] p-3">
                    <div className="font-semibold text-[#11182B]">{(preview.increment_recipients || []).length} employee(s) will get an increment letter</div>
                    <div className="text-[#98A1B2] text-xs mt-0.5">{(preview.excluded || []).length} excluded (no increment this cycle)</div>
                  </div>
                  <p className="text-xs text-[#5A6377]">Letters are generated after locking, from the <strong>Generate Letters</strong> button.</p>
                </div>
              ) : <p className="text-sm text-[#5A6377]">Loading…</p>}
              <DialogFooter>
                <Button variant="outline" onClick={() => { setLockOpen(false); setPreview(null); }}>Cancel</Button>
                <Button data-testid="confirm-lock-button" onClick={confirmLock} disabled={lockBusy}>{lockBusy ? 'Locking…' : `Lock ${latestYear}`}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </main>
      </div>
    </div>
  );
}
